import {useMemo} from "react";
import {Area, Line} from "recharts";
import {BaseChart, ChartComponent} from "./BaseChart";
import {CHART_ITEM_HIDDEN_LABEL} from "./BaseChartTooltip";
import {FieldResult} from "../../../types/domain/fieldResult";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {useMeasureSystemUtils} from "../../../hooks/measureSystemUtilsHooks";
import {useTranslation} from "react-i18next";
import {MeasureSystemUtils} from "../../../types/domain/measureSystemUtils";
import dayjs from "dayjs";
import {useAtomValue} from "jotai";
import {activeFieldLoadable} from "../../../store/activeFieldStore";
import {Field} from "../../../types/domain/field";
import {irrigationDefaults} from "../../../utils/irrigationDefaults";

export const VirtualSoilMoistureProbeChart: ChartComponent = ({data}) => {
    const {t, i18n} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const fieldState = useAtomValue(activeFieldLoadable);

    const chartData = useMemo(
        () =>
            fieldState?.state === "hasData" && fieldState?.data
                ? data.map((d) => mapFieldResultsToChartData(d, msUtils, fieldState.data!, i18n))
                : [],
        [data, fieldState, msUtils, i18n],
    );
    const ratioUnit = msUtils.getMoistureUnits();

    return (
        <BaseChart
            title={t("insights.charts.virtual_soil_moisture_probe.title")}
            yLabel={ratioUnit}
            data={chartData}
            helpPopoverTranslationKey="help.popover.fields.virtual_soil_moisture"
        >
            <Area
                type="monotone"
                dataKey="fieldCapacitySoilMoistureRootZone"
                name={t("insights.charts.virtual_soil_moisture_probe.area1_name")}
                unit={ratioUnit}
                yAxisId="left"
                fill="#C2FCC0"
                strokeWidth={0}
                isAnimationActive={false}
                baseValue="dataMax"
            />
            <Area
                type="monotone"
                dataKey="criticalSoilMoistureRootZone"
                name={t("insights.charts.virtual_soil_moisture_probe.area2_name")}
                unit={ratioUnit}
                yAxisId="left"
                stroke="#EA463C"
                fill="#EA463C"
                strokeWidth={2}
                isAnimationActive={false}
            />

            <Area
                type="monotone"
                dataKey="saturatedSoilMoistureRootZone"
                name={t("insights.charts.virtual_soil_moisture_probe.area3_name")}
                unit={ratioUnit}
                yAxisId="left"
                stroke="#A1AEE3"
                fill="#E2E7F8"
                strokeDasharray="5 5"
                strokeWidth={2}
                isAnimationActive={false}
                baseValue="dataMax"
            />
            <Area
                type="monotone"
                dataKey="saturatedSoilMoistureRootZoneMax"
                name={CHART_ITEM_HIDDEN_LABEL}
                legendType="none"
                yAxisId="left"
                stroke="#A1AEE3"
                fill="#E2E7F8"
                strokeDasharray="5 5"
                strokeWidth={2}
                isAnimationActive={false}
                baseValue="dataMax"
            />
            <Area
                type="monotone"
                dataKey="refillPoint"
                name={t("insights.charts.virtual_soil_moisture_probe.refillpoint")}
                unit={ratioUnit}
                legendType="none"
                yAxisId="left"
                stroke="#c18024"
                fill="#F9C36B"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
                baseValue="dataMax"
            />
            <Line
                type="monotone"
                dataKey="soilMoistureRootZone"
                name={t("insights.charts.virtual_soil_moisture_probe.line2_name")}
                unit={ratioUnit}
                yAxisId="left"
                stroke="#5161C1"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="wittingPoint"
                name={t("insights.charts.virtual_soil_moisture_probe.line1_name")}
                unit={ratioUnit}
                legendType="none"
                yAxisId="left"
                stroke="#EA463C"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
            />
            <Area
                type="monotone"
                dataKey="soilMoisturePercentiles"
                name={CHART_ITEM_HIDDEN_LABEL}
                unit={ratioUnit}
                legendType="none"
                yAxisId="left"
                stroke="#545A6E"
                fill="#545A6E"
                opacity="0.35"
                isAnimationActive={false}
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (
    r: FieldResult,
    ms: MeasureSystemUtils,
    f: Field,
    i18n: {language: string},
): DataProps => {
    const defaultIrrigationCriterium =
        irrigationDefaults.find((defaultVal) => defaultVal.id === f.irrigationTypeId)?.irrigation_criterium_min ?? 0;

    const refillPointUpperLimit =
        (f?.irrigationCriteria ? f.irrigationCriteria : defaultIrrigationCriterium) *
            (r.aggregatedProps.theta_fc_sub - r.aggregatedProps.critical_soil_moisture_root_zone) +
        r.aggregatedProps.critical_soil_moisture_root_zone;

    return {
        date: dayjs(r.date).locale(i18n.language).toDate(),
        refillPoint: [
            ms.getMoisture(r.aggregatedProps.critical_soil_moisture_root_zone),
            Math.round(refillPointUpperLimit * 1000) / 1000,
        ],
        soilMoistureRootZone: ms.getMoisture(r.aggregatedProps.soil_moisture_root_zone_today),
        soilMoisturePercentiles: [
            ms.getMoisture(r.aggregatedProps.soil_moisture_percentile5),
            ms.getMoisture(r.aggregatedProps.soil_moisture_percentile95),
        ],
        criticalSoilMoistureRootZone: ms.getMoisture(r.aggregatedProps.critical_soil_moisture_root_zone),
        wittingPoint: ms.getMoisture(r.aggregatedProps.theta_wp_sub),
        fieldCapacitySoilMoistureRootZone: [
            ms.getMoisture(r.aggregatedProps.critical_soil_moisture_root_zone),
            ms.getMoisture(r.aggregatedProps.theta_fc_sub),
        ],
        saturatedSoilMoistureRootZone: [
            ms.getMoisture(r.aggregatedProps.theta_fc_sub),
            ms.getMoisture(r.aggregatedProps.theta_sat_sub),
        ],
        saturatedSoilMoistureRootZoneMax: ms.getMoisture(r.aggregatedProps.theta_sat_sub),
    };
};

export interface DataProps extends TimeSeriesDataProps {
    soilMoistureRootZone: number;
    soilMoisturePercentiles: number[];
    criticalSoilMoistureRootZone: number;
    wittingPoint: number;
    fieldCapacitySoilMoistureRootZone: number[];
    saturatedSoilMoistureRootZone: number[];
    saturatedSoilMoistureRootZoneMax: number;
    refillPoint: number[];
}
